import { CalculationTraceSection as TraceSectionData, CalculationTraceStep } from "@/lib/api";

interface Props {
  title: string;
  section: TraceSectionData;
}

const FLAG_LABELS: Record<string, string> = {
  floorApplied: "Mindestverbrauch angewendet",
  spikesCapped: "Ausreißer gekappt",
  oosAdjusted: "OOS-korrigiert",
  minStockFloorApplied: "Mindestbestand-Floor",
  overduePos: "Enthält überfällige Bestellungen",
  deadPosIgnored: "Zulauf wegen Alters nicht verrechnet",
  moqApplied: "MOQ angewendet",
  packRounded: "VE-gerundet",
  coverageCapped: "Reichweite gekappt",
  escalatedByComponent: "Durch Komponente angehoben",
  derivedFromBom: "Aus Stückliste abgeleitet",
  ownRecommendationWins: "Eigenempfehlung höher als Stücklisten-Bedarf",
  safetyStockApplied: "Sicherheitsbestand berücksichtigt",
  safetyStockGuardTripped: "Zu wenige Verbrauchstage für Sicherheitsbestand",
  frozen: "Eingefroren (OOS)",
};

function formatValue(value: CalculationTraceStep["value"]): string {
  if (value === null) return "–";
  if (typeof value === "number") return value.toLocaleString("de-DE", { maximumFractionDigits: 4 });
  return value;
}

export default function CalculationTraceSection({ title, section }: Props) {
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <span className="font-mono text-[10px] text-ink-soft">
          {section.formulaId} v{section.formulaVersion}
        </span>
      </div>

      <table className="w-full text-xs border-separate border-spacing-y-0">
        <colgroup>
          <col className="w-56" />
          <col />
          <col className="w-28" />
        </colgroup>
        <tbody className="text-ink-muted">
          {section.steps.map((step, i) => (
            <tr key={i} className="align-top">
              <td className="py-1 pr-3 border-t border-surface-3">{step.label}</td>
              <td className="py-1 pr-3 font-mono text-[11px] border-t border-surface-3">{step.expr}</td>
              <td className="py-1 text-right tabular-nums border-t border-surface-3">{formatValue(step.value)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={2} className="pt-1.5 text-right font-semibold border-t border-line text-ink">
              Ergebnis
            </td>
            <td className="pt-1.5 text-right tabular-nums font-semibold border-t border-line text-ink">
              {formatValue(section.result)}
            </td>
          </tr>
        </tbody>
      </table>

      {section.flags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {section.flags.map((flag) => (
            <span
              key={flag}
              className="inline-flex items-center rounded-full border border-line bg-surface-2 px-2 py-0.5 text-[10px] text-ink-soft"
            >
              {FLAG_LABELS[flag] ?? flag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
